import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ShieldCheck, Truck, Lock, Banknote, Sparkles,
  CheckCircle2, ChevronDown, Zap, Star, Clock, TrendingDown, Heart,
  XCircle, Flame,
} from 'lucide-react';

const USPS = [
  {
    icon: Lock,
    title: 'Private &amp; discreet',
    body: 'Plain, unmarked packaging. No card processors. No paper trail to surprise you on a bank statement.',
  },
  {
    icon: Banknote,
    title: 'No card fees, no surprises',
    body: 'Pay direct from your bank by PayID. Flat pricing — no subscription traps, no auto-charges.',
  },
  {
    icon: Truck,
    title: 'Fast Australia-wide',
    body: 'Ships within 1–2 business days of payment. Metro 2–4 days, regional 3–7 days.',
  },
  {
    icon: ShieldCheck,
    title: 'You\'re in control',
    body: 'Order when you need it. Stop when you want. No GP gatekeeping, no monthly minimums.',
  },
];

const STEPS = [
  { n: '01', title: 'Sign up free', body: 'Quick account, verify your mobile — under 60 seconds.' },
  { n: '02', title: 'Choose your program', body: 'See your full options once you\'re inside.' },
  { n: '03', title: 'Pay by PayID', body: 'Transfer the amount with the reference we generate. No card details needed.' },
  { n: '04', title: 'Start losing', body: 'Order ships the next business day. Most people see results in the first 30 days.' },
];

const FAQS = [
  {
    q: 'How much does it cost?',
    a: 'Less than the junk food bill most Aussies don\'t even notice. Pricing is shown in full once you sign in — no hidden fees, no subscription traps.',
  },
  {
    q: 'How is this paid?',
    a: 'Bank-to-bank PayID transfer from your own banking app. No card processors, no recurring charges, no chargebacks. You stay in control.',
  },
  {
    q: 'How fast will I see results?',
    a: 'Most people notice changes within the first 2–4 weeks. Faster than most diets, with significantly less willpower required.',
  },
  {
    q: 'Is it discreet?',
    a: 'Yes. Plain, unbranded packaging. Discreet sender details. Nothing on your bank statement that would raise eyebrows.',
  },
  {
    q: 'Do I have to keep buying forever?',
    a: 'No. Order when you need it, stop when you want. There\'s no auto-renewal and no minimum monthly spend.',
  },
  {
    q: 'How old do I need to be?',
    a: 'You must be 18+ to create an account and place an order. We verify your mobile number as part of signup.',
  },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <main className="bg-[#0a0a0a] text-white" data-testid="landing-page">
      <HeroSection />
      <CostComparisonSection />
      <UspGrid />
      <HowItWorks />
      <SocialProof />
      <FaqSection items={FAQS} openIdx={openFaq} setOpenIdx={setOpenFaq} />
      <FinalCta />
    </main>
  );
};

// --- HERO — channels the ad ---
const HeroSection = () => (
  <section className="relative overflow-hidden border-b border-red-900/30" data-testid="landing-hero">
    <div className="absolute inset-0 opacity-30 pointer-events-none">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_25%,rgba(239,68,68,0.3),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_75%,rgba(34,197,94,0.15),transparent_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(239,68,68,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(239,68,68,0.05)_1px,transparent_1px)] bg-[size:64px_64px]" />
    </div>

    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-24 sm:pt-32 sm:pb-32">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-full text-[11px] tracking-[0.18em] uppercase text-red-300 font-bold">
        <Flame size={11} /> Australian · 18+ · PayID native
      </div>

      <h1 className="mt-7 font-black tracking-tight text-5xl sm:text-7xl lg:text-8xl leading-[0.92] max-w-5xl">
        Lose the weight.<br />
        <span className="text-emerald-400">Without the wait.</span>
      </h1>

      <p className="mt-7 text-base sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
        Stop wasting money on takeaway, fad diets and gym subscriptions you don't use. FAT BUSTERS is the discreet Aussie treatment program that actually works — paid by PayID, shipped to your door, no card processor middleman.
      </p>

      <div className="mt-9 flex items-center gap-3 flex-wrap">
        <Link
          to="/account"
          data-testid="hero-cta-signup"
          className="group inline-flex items-center gap-2 px-7 py-4 bg-red-500 hover:bg-red-400 active:bg-red-600 text-white font-bold rounded-full text-sm tracking-wide shadow-2xl shadow-red-500/30 transition-all hover:scale-[1.02]"
        >
          Start losing — sign up free
          <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
        </Link>
        <Link
          to="/account"
          data-testid="hero-cta-prices"
          className="inline-flex items-center gap-2 px-5 py-4 text-slate-300 hover:text-white font-semibold rounded-full text-sm border border-white/15 hover:border-white/40 transition-colors"
        >
          See pricing inside
        </Link>
      </div>

      <div className="mt-12 flex items-center gap-6 text-[11px] text-slate-400 flex-wrap">
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> No GP gatekeeping</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> No card fees</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> No subscription lock-in</span>
        <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-400" /> Plain packaging</span>
      </div>
    </div>
  </section>
);

// --- COST COMPARISON — direct callback to the ad ---
const CostComparisonSection = () => (
  <section className="relative border-b border-white/5 py-20 sm:py-28 overflow-hidden" data-testid="landing-cost">
    <div className="max-w-5xl mx-auto px-5 sm:px-8">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-[11px] tracking-[0.18em] uppercase text-emerald-300 font-bold">
          <TrendingDown size={11} /> Do the math
        </div>
        <h2 className="mt-5 text-3xl sm:text-5xl font-black tracking-tight leading-tight">
          Your junk food bill is <span className="text-red-500">more expensive</span><br className="hidden sm:inline" /> than this program.
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
          Most people are already spending more on uber-eats, snacks and "just one coffee" than what FAT BUSTERS costs each month.
        </p>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
        {/* Junk food card */}
        <div className="bg-gradient-to-br from-red-950/40 to-red-900/10 border border-red-500/30 rounded-3xl p-7 sm:p-8 relative overflow-hidden" data-testid="cost-junk">
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase text-red-400 bg-red-500/10 px-2 py-1 rounded-full">
            <XCircle size={11} /> The trap
          </div>
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-400">Average Aussie · Per month</p>
          <p className="mt-3 text-5xl sm:text-6xl font-black text-red-400 font-mono">$300<span className="text-2xl text-red-400/60">+</span></p>
          <p className="mt-2 text-sm text-slate-400">on junk food, takeaway &amp; snacks</p>
          <ul className="mt-5 space-y-2 text-sm text-slate-300">
            <li className="flex items-center gap-2"><XCircle size={14} className="text-red-500 flex-shrink-0" /> Calories you don't want</li>
            <li className="flex items-center gap-2"><XCircle size={14} className="text-red-500 flex-shrink-0" /> No willpower help</li>
            <li className="flex items-center gap-2"><XCircle size={14} className="text-red-500 flex-shrink-0" /> Guilt afterwards</li>
            <li className="flex items-center gap-2"><XCircle size={14} className="text-red-500 flex-shrink-0" /> Weight stays the same</li>
          </ul>
        </div>

        {/* Treatment card */}
        <div className="bg-gradient-to-br from-emerald-950/40 to-emerald-900/10 border-2 border-emerald-500/40 rounded-3xl p-7 sm:p-8 relative overflow-hidden shadow-2xl shadow-emerald-500/10" data-testid="cost-treatment">
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.12em] uppercase text-emerald-300 bg-emerald-500/15 px-2 py-1 rounded-full">
            <Heart size={11} /> The fix
          </div>
          <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-300">FAT BUSTERS · Per month</p>
          <p className="mt-3 text-5xl sm:text-6xl font-black text-emerald-400 font-mono">from $250</p>
          <p className="mt-2 text-sm text-slate-400">discreet weight-loss program</p>
          <ul className="mt-5 space-y-2 text-sm text-slate-200">
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" /> Suppresses appetite naturally</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" /> Visible results in weeks</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" /> Eat less &amp; save money</li>
            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" /> Real Aussie support</li>
          </ul>
        </div>
      </div>

      <div className="mt-10 text-center">
        <Link
          to="/account"
          data-testid="cost-cta"
          className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-[#0a0a0a] font-bold rounded-full text-sm tracking-wide shadow-xl shadow-emerald-500/30 transition-all hover:scale-[1.02]"
        >
          Start saving money &amp; losing weight <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  </section>
);

// --- USPS (no peptide references) ---
const UspGrid = () => (
  <section className="border-b border-white/5 py-20 sm:py-28" data-testid="landing-usps">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-500">Why FAT BUSTERS</div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Built for Aussies. Not the medical industry.</h2>
        <p className="mt-3 text-sm text-slate-400">No prescriptions to chase. No clinics to visit. No insurance forms. You order, you pay, it arrives.</p>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {USPS.map((u) => {
          const Icon = u.icon;
          return (
            <div key={u.title} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-red-500/40 transition-colors">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Icon size={18} className="text-red-500" />
              </div>
              <h3 className="mt-4 text-base font-bold" dangerouslySetInnerHTML={{ __html: u.title }} />
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{u.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// --- HOW IT WORKS ---
const HowItWorks = () => (
  <section className="border-b border-white/5 py-20 sm:py-28" data-testid="landing-how">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="max-w-2xl">
        <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-500">From signup to first dose</div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Takes about 90 seconds to start.</h2>
      </div>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STEPS.map((s, idx) => (
          <div key={s.n} className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6">
            <div className="absolute -top-3 -left-1 text-7xl font-black text-red-500/15 leading-none select-none">{s.n}</div>
            <div className="relative">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-red-400">Step {idx + 1}</div>
              <h3 className="mt-2 text-base font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- SOCIAL PROOF ---
const SocialProof = () => (
  <section className="border-b border-white/5 py-20 sm:py-24" data-testid="landing-proof">
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-3xl p-8 sm:p-12">
        <div className="flex items-center gap-1.5 text-emerald-400">
          {[1,2,3,4,5].map((i) => <Star key={i} size={16} fill="currentColor" />)}
        </div>
        <blockquote className="mt-5 text-xl sm:text-2xl font-semibold leading-tight max-w-3xl">
          "I've spent thousands on diets, gyms and meal kits over the years. FAT BUSTERS is the first thing that's actually worked — and it's cheaper than my Friday-night takeaway habit."
        </blockquote>
        <div className="mt-6 text-sm text-slate-400">
          — Verified buyer · Down 11kg in 14 weeks
        </div>
      </div>
    </div>
  </section>
);

// --- FAQ ---
const FaqSection = ({ items, openIdx, setOpenIdx }) => (
  <section className="border-b border-white/5 py-20 sm:py-28" data-testid="landing-faq">
    <div className="max-w-3xl mx-auto px-5 sm:px-8">
      <div className="text-[11px] font-bold tracking-[0.18em] uppercase text-red-500">FAQ</div>
      <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">The honest answers.</h2>

      <div className="mt-8 space-y-2">
        {items.map((item, idx) => {
          const open = openIdx === idx;
          return (
            <button
              key={item.q}
              type="button"
              onClick={() => setOpenIdx(open ? null : idx)}
              data-testid={`landing-faq-${idx}`}
              className={`w-full text-left rounded-2xl border transition-colors ${
                open ? 'bg-white/[0.04] border-red-500/40' : 'bg-white/[0.02] border-white/10 hover:border-white/20'
              }`}
            >
              <div className="px-5 sm:px-6 py-4 flex items-center justify-between gap-4">
                <span className="text-sm sm:text-base font-semibold">{item.q}</span>
                <ChevronDown size={16} className={`text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180 text-red-400' : ''}`} />
              </div>
              {open && (
                <div className="px-5 sm:px-6 pb-5 text-sm text-slate-400 leading-relaxed">
                  {item.a}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  </section>
);

// --- FINAL CTA ---
const FinalCta = () => (
  <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="landing-final-cta">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.18),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(239,68,68,0.18),transparent_55%)]" />
    </div>
    <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-[11px] tracking-[0.18em] uppercase text-emerald-300 font-bold">
        <Zap size={11} /> 60-second signup
      </div>
      <h2 className="mt-6 text-4xl sm:text-6xl font-black tracking-tight leading-[0.95]">
        Lose the weight.<br />
        <span className="text-emerald-400">Starting today.</span>
      </h2>
      <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-md mx-auto">
        Create an account, verify your number, and you're in. Free to join. No card details required.
      </p>
      <div className="mt-9">
        <Link
          to="/account"
          data-testid="final-cta-signup"
          className="group inline-flex items-center gap-2 px-9 py-5 bg-red-500 hover:bg-red-400 active:bg-red-600 text-white font-bold rounded-full text-base tracking-wide shadow-2xl shadow-red-500/30 transition-all hover:scale-[1.02]"
        >
          Create my account
          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
      <div className="mt-8 text-[11px] text-slate-500 inline-flex items-center gap-1.5">
        <Clock size={11} /> Most people are signed up and ordering in under 2 minutes.
      </div>
    </div>
  </section>
);

export default LandingPage;
